import { Helmet } from 'react-helmet-async';
import { SITE_NAME, SITE_URL } from './SEO';

interface JsonLdProps {
  data: Record<string, unknown>;
}

/**
 * JSON for an inline <script>: `<` is escaped so content containing `</script>` can't close the
 * tag, and U+2028/U+2029 so the result is also valid inside a JavaScript string.
 */
export function serializeJsonLd(data: Record<string, unknown>): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

export function JsonLd({ data }: JsonLdProps) {
  return (
    <Helmet>
      <script type="application/ld+json">{serializeJsonLd(data)}</script>
    </Helmet>
  );
}

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  email: 'support@resida.me',
  description:
    'Rental management platform for independent landlords with 1 to 20 units.',
};

export const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
};

export const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'I only have a couple of properties. Is this overkill?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "The opposite — landlords with one or two properties are exactly who Resida is for. You don't have a property management team, so every follow-up, every receipt, and every lease renewal is on you. Resida keeps it organized so things don't slip through the cracks.",
      },
    },
    {
      '@type': 'Question',
      name: 'There are free tools out there. Why would I pay?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Free tools cover the basics — listing a property, collecting rent. But you end up stitching together three or four apps, manually tracking expenses, and piecing together records at tax time. Resida puts everything in one place and does the expense categorization for you. The free trial lets you see if the time savings are worth it before you pay anything.',
      },
    },
    {
      '@type': 'Question',
      name: 'How does the AI expense tracking work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Take a photo of a receipt or upload an image. Resida reads the vendor name, amount, and date, then suggests which IRS Schedule E category it belongs to (repairs, insurance, utilities, etc.). You review the suggestion and confirm. When tax season comes, your expenses are already categorized and ready to export.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do my tenants need to install anything?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. Tenants use a web portal that works on any device — phone, tablet, or computer. They can pay rent, submit maintenance requests with photos, and review their lease. Nothing to download.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is my financial data secure?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Payment processing uses industry-standard encryption. Resida never stores raw card or bank account numbers. All lease, communication, and financial data is accessible only to authorized users on your account.',
      },
    },
  ],
};
